import { useCallback, useState } from 'react';
import { AliasManager } from 'lib/asset-aliases';
import type { AssetAlias, DevelopmentAreaResponse } from 'types/api-responses';

/** The part of the page's development area the aliases read. */
type AliasedDevelopmentArea = Pick<DevelopmentAreaResponse, 'name' | 'aliases'>;

interface DevelopmentAreaAliasesProps {
  developmentArea: AliasedDevelopmentArea;
}

/**
 * The development area page header's aliases: pills and Add Alias. The list comes with the page's own
 * payload. An add or a remove answers with the new list, which is layered over that payload instead of
 * refetching it: the page clears and reloads the whole area on a refetch. A refetch brings a new payload
 * object, which retires the layered list, so the server's answer wins as soon as it is read again.
 * @param {DevelopmentAreaAliasesProps} props - The development area the page is on.
 * @return {JSX.Element} The alias controls.
 */
export function DevelopmentAreaAliases({
  developmentArea,
}: DevelopmentAreaAliasesProps) {
  const [layered, setLayered] = useState<{
    base: AliasedDevelopmentArea;
    aliases: AssetAlias[];
  } | null>(null);
  const aliases =
    layered?.base === developmentArea
      ? layered.aliases
      : developmentArea.aliases;

  const handleChange = useCallback(
    (next: AssetAlias[]) =>
      setLayered({ base: developmentArea, aliases: next }),
    [developmentArea]
  );

  return (
    <AliasManager
      aliases={aliases}
      aliasesPath={`/development_areas/${encodeURIComponent(developmentArea.name)}/aliases`}
      recordLabel="development area"
      recordNumber={developmentArea.name}
      onChange={handleChange}
    />
  );
}
